/**
 * GlobalModal - Centralized modal orchestrator
 *
 * Listens to the Zustand UI store and renders the appropriate modal
 * based on the current modal type and props.
 *
 * @example
 * // In your app root
 * <GlobalModal />
 *
 * // Anywhere in the app
 * const openModal = useUIStore((s) => s.openModal)
 * openModal("new-company", { defaultValues: { name: "Acme" } })
 */

import { useModal } from '@/stores/ui-store';
import { ConfirmDialog } from '@/components/blocks/feedback/ConfirmDialog';

/**
 * Global modal component that connects to Zustand store
 */
export function GlobalModal() {
  const { modal, close } = useModal();

  if (!modal.type) {
    return null;
  }

  // Route to specific modal components based on type
  switch (modal.type) {
    case 'new-company':
      // TODO: Implement NewCompanyModal
      return null;

    case 'new-site':
      // TODO: Implement NewSiteModal
      return null;

    case 'new-unit':
      // TODO: Implement NewUnitModal
      return null;

    case 'new-personnel':
      // TODO: Implement NewPersonnelModal
      return null;

    case 'new-action':
      // TODO: Implement NewActionModal
      return null;

    case 'new-report':
      // TODO: Implement NewReportModal
      return null;

    case 'entity-picker':
      // TODO: Implement EntityPickerModal
      return null;

    case 'confirm-delete': {
      const {
        title = 'Conferma',
        message = 'Sei sicuro?',
        confirmLabel = 'Conferma',
        cancelLabel = 'Annulla',
        variant = 'danger',
        onConfirm = () => {},
        isLoading = false,
      } = modal.props;

      const handleConfirm = async () => {
        try {
          await onConfirm();
        } finally {
          close();
        }
      };

      return (
        <ConfirmDialog
          open={modal.type === 'confirm-delete'}
          onOpenChange={(open) => !open && close()}
          title={title}
          message={message}
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          variant={variant}
          onConfirm={handleConfirm}
          isLoading={isLoading}
        />
      );
    }

    default:
      // Type-safe exhaustive check
      return null as never;
  }
}
